import { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import config from '../config';

/**
 * Custom hook that wraps fetch() to automatically include
 * the Firebase Auth Bearer token in all API requests.
 */
export const useAuthFetch = () => {
  const { getToken } = useAuth();

  const authFetch = useCallback(
    async (endpoint: string, options: RequestInit = {}): Promise<Response> => {
      const token = await getToken();

      if (!token) {
        throw new Error('Not authenticated. Please sign in.');
      }

      const url = endpoint.startsWith('http')
        ? endpoint
        : `${config.API_BASE_URL}${endpoint}`;

      const headers = new Headers(options.headers);
      headers.set('Authorization', `Bearer ${token}`);

      // Identity reveal is opt-in and session-bound; backend uses this to shape names.
      const revealEnabled = sessionStorage.getItem('xrank_identity_reveal_enabled') === 'true';
      const revealExpires = Number(sessionStorage.getItem('xrank_identity_reveal_expires_at') || '0');
      if (revealEnabled && Number.isFinite(revealExpires) && revealExpires > Date.now()) {
        headers.set('X-Identity-Reveal', '1');
      }

      // Only set Content-Type for non-FormData requests
      if (!(options.body instanceof FormData)) {
        if (!headers.has('Content-Type')) {
          headers.set('Content-Type', 'application/json');
        }
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Handle quota exceeded (429) with a friendly error
      if (response.status === 429) {
        const data = await response.json().catch(() => null);
        const message = data?.detail?.message || data?.detail || 'Usage limit reached. Please upgrade your plan.';
        throw new QuotaExceededError(message, data?.detail);
      }

      return response;
    },
    [getToken]
  );

  return authFetch;
};

/**
 * Custom error class for quota exceeded (429) responses.
 * Contains structured detail from the backend.
 */
export class QuotaExceededError extends Error {
  detail: any;
  constructor(message: string, detail?: any) {
    super(message);
    this.name = 'QuotaExceededError';
    this.detail = detail;
  }
}
